<template>
	<div>
		<div class="d-flex flex-row align-items-center justify-content-between">
			<h3 v-if="!isWizardInstance">Employee Classifications</h3>

			<button v-on:click="newClassification" class="btn btn-success">
				<i class="fa fa-plus"></i> 
				New Classification
			</button>
		</div>

		<onboarding-card v-if="!isWizardInstance" title="Let's add some classifications" name="Employee Classifications" setting-key="employeeClassifications" v-on:sectionComplete="setupCompleteModalVisible = true" :next-link="nextSetupLink">
			<p>Employee Classifications allow you to set differing pay rates and cost values for different types of employees. This is important for calculating labour costing on projects, as well as for payroll purposes.</p>

			<button v-on:click="newClassification" class="btn btn-success">
				<i class="fa fa-plus"></i> 
				New Classification
			</button>
		</onboarding-card>
		
		<table-list
			key="classificationsList"
			:records="mutatedClassifications"
			:mobile-breakpoint="$store.tableListMobileBreakpoint"
			:fields="fields"
			record-name="Classification"
			:hide-record-count="isWizardInstance"
			:has-edit="true"
			id-field="slug"
			v-on:edit="editRecord">

			<field v-if="unionsOptions(true).length > 0" label="Filter by Compensation Group" col-class="col-auto" :params="{type:'select',options:unionsOptions(false)}" v-model="unionFilter"></field>
		</table-list>

		<modal-dialog
			:modal-visible="editModal.visible"
			:title="editModal.title"
			confirm-button-text="Save"
			modal-size="modal-lg"
			v-on:confirm="save"
			v-on:closeModal="editModal.visible = false">

			<field label="Title" col-class="col-md-3 col-sm-4" v-model="editModal.payload.title" ></field>

			<field 
				label="Classification Colour" 
				col-class="col-md-3 col-sm-4" 
				v-model="editModal.payload.color_hex" 
				:params="{type: 'color'}">
			</field>

			<field v-if="unionsOptions(true).length > 0"
				label="Compensation Group" 
				col-class="col-md-3 col-sm-4" 
				v-model="editModal.payload.union_id" 
				:params="{type:'select',options: unionsOptions(true)}"
				v-on:change="unionSelect">
			</field>

			<field v-if="groupOptions.length > 0"
				label="Charge Out Group" 
				col-class="col-md-3 col-sm-4" 
				v-model="editModal.payload.group_id" 
				:params="{type: 'select',options:groupOptions}">

				<p class="text-muted">Charge out groups allow you to group classifications together for billing purposes</p>
			</field>

			<template v-if="editModal.attachedToUnion && !isWizardInstance">
				<form-group label="Pay Rates" col-class="col-md-3 col-sm-4">
					<p>Since this classification is attached to a compensation group, the pay rates may be modified there.

						<router-link :to="'/employees/settings/unions/'+editModal.record.Union.slug">Go to {{editModal.record.Union.title}} compensation group</router-link>
					</p>
				</form-group>
			</template>
			<template v-else>

				<field label="Base Hourly Rate ($/hr)" col-class="col-md-3 col-sm-4" v-model="editModal.payload.default_hourly_rate" :params="{type: 'number', step: 0.01, min: 0}"></field>

				<form-group label="Total Cost/Hr" col-class="col-md-3 col-sm-4">
					<div class="d-flex flex-row">

						<field 
							v-model="editModal.payload.default_cost_hr" 
							:params="{type: 'number', step: 0.01, min: 0,placeholder:'Reg. Time'}">
							<label class="text-muted">
								Reg. Time
							</label>
						</field>

						<field
							v-if="timeLogSettings.uses_overtime_15x == 1"
							
							v-model="editModal.payload.ot15_cost_hr" 
							:params="{type: 'number', step: 0.01, min: 0,placeholder:'OT 1.5x'}">
							<label class="text-muted">
								OT 1.5x
							</label>
						</field>

						<field 
							v-if="timeLogSettings.uses_overtime_2x == 1"
							
							v-model="editModal.payload.ot2_cost_hr" 
							:params="{type: 'number', step: 0.01, min: 0,placeholder:'OT 2x'}">
							<label class="text-muted">
								OT 2x
							</label>
						</field>

						<field 
							v-if="timeLogSettings.uses_overtime_25x == 1"
							
							v-model="editModal.payload.ot25_cost_hr" 
							:params="{type: 'number', step: 0.01, min: 0,placeholder:'OT 2.5x'}">
							<label class="text-muted">
								OT 2.5x
							</label>
						</field>
					
					</div>
				</form-group>

			</template>

			<div class="row" v-if="editModal.showDelete">
				<div class="col-sm-3" style="padding-top: 3px;">

					<button type="button" v-if="editModal.record.User && editModal.record.User.length > 0" class="btn btn-outline-danger float-left" disabled><i class="fa fa-trash"></i> Delete</button>

					<button type="button" v-else class="btn btn-outline-danger float-left" v-on:click="deleteItem"><i class="fa fa-trash"></i> Delete</button>

					
				</div>
				<div v-if="editModal.record.User && editModal.record.User.length > 0" class="col-sm-9 text-muted">
					Classifications may only be deleted if no employee has been assigned to it.
				</div>
			</div>
			

		</modal-dialog>

		<modal-dialog
			:modal-visible="setupCompleteModalVisible"
			title="Initial Setup Complete!"
			:submit-visible="false"
			cancel-button-text="Finish Setup"
			v-on:closeModal="finishSetup">

			<div class="alert alert-info text-center">
				<h2 style="margin-bottom: 0">You're Ready to Go!</h2>

			</div>
			<p>Your company is all set up and ready to go! From here you can do any of the following:</p>

				<ul>
					<li><router-link to="/project/new/details">Create your first <strong>project</strong></router-link></li>
					<li><router-link to="/service/new/details">Create your first <strong>service</strong></router-link></li>
					<li><router-link to="/contacts">Add contacts & customers</router-link></li>
					<li><router-link to="/employees/list">Create employee accounts</router-link></li>
					<li><router-link to="/company/unions">Set up compensation groups & schedule details</router-link></li>
					<li><router-link to="/equipment/list">Start tracking equipment</router-link></li>
					<li><router-link to="/settings">View the rest of your settings</router-link></li>
				</ul>

		</modal-dialog>

	</div>
</template>

<script>
import FormGroup from "@/components/FormGroup"
import Field from "@/components/Field"
import TableList from "@/components/TableList"
import ModalDialog from "@/components/ModalDialog"
import OnboardingCard from "@/components/OnboardingCard"

export default {

	name: 'EmployeeClassifications',
	components: {
		TableList,
		ModalDialog,
		FormGroup,
		Field,
		OnboardingCard
	},
	props: ["isWizardInstance"],
	data() {
		return {
			unions: [],
			groups: [],
			unionFilter: null,
			editModal: {
				visible: false,
				title: "Edit Classification",
				payload: {
					title: "",
					default_hourly_rate: null,
					charge_out_rate: null
				},
				record: {},
				showDelete: true,
				attachedToUnion: false
			},
			timeLogSettings: {},
			setupCompleteModalVisible: false
		}
	},
	computed: {
		fields() { 
			let fields = [
				{
					label: "Title",
					key: "title"
				},
				{
					label: "No. Employees",
					key: "employeeCount",
					align:'center'
				},
				/*{
					label: "Hourly Rate",
					key: "default_hourly_rate",
					align: "right"
				},*/
				{
					label: "Charge Out Group",
					key: "chargeOutGroup"
				}
			]

			if (this.unions.length > 0) {
				fields.splice(1,0,{
					label: "Comp. Group",
					key: "unionName"
				})
			}

			return fields
		},
		nextSetupLink() {
			if ("unions" in this.$store && this.$store.unions && this.$store.unions.length > 0)
				return "/employees/settings/unions"
			else
				return false
		},
		mutatedClassifications() {
			let vm = this

			let classifications = []

			if (this.unionFilter > 0) {
				classifications = this.$store.employeeClassifications.filter(classification => {
					if (classification.union_id == vm.unionFilter)
						return true
				})
			}
			else if (this.$store.employeeClassifications)
				classifications = this.$store.employeeClassifications

			return classifications.map(classification => {

				classification.unionName = ""
				if (classification.Union)
					classification.unionName = classification.Union.union_name

				classification.chargeOutGroup = ""
				if (classification.ClassificationGroup)
					classification.chargeOutGroup = classification.ClassificationGroup.label

				classification.employeeCount = ""
				if ("User" in classification) {
					classification.employeeCount = classification.User.reduce((acc,user) => {
						if (user.UserPermission && user.UserPermission.length > 0 && !user.UserPermission[0].disconnect_date && user.UserPermission[0].invite_accepted)
							return acc + 1
						return acc
					},0)
				}

				return classification
			})
		},
		groupOptions() {
			return this.optionify(this.groups.map(group => {
				let rate = +group.charge_out_rate

				group.display = group.label+" (billed at $"+rate.toCurrency()+"/hr)"
				return group
			}),"display")
		}
	},
	methods: {
		finishSetup() {
			this.$store.companySetupMode = false
			this.setupCompleteModalVisible = false
		},
		unionsOptions(excludeAll) {
			let unions = []

			if (!excludeAll)
				unions.push({id: 0,union_name: "All"})

			unions = unions.concat(this.unions)

			return this.optionify(unions,"union_name")
		},
		unionSelect(value) {
			if (value > 0) {
				this.editModal.attachedToUnion = true
				this.editModal.record.Union = this.unions.find(union => union.id == value)
			}
			else {
				this.editModal.record.Union = false
				this.editModal.attachedToUnion = false
			}
		},
		editRecord(record) {
			this.editModal.payload = record
			this.editModal.visible = true
			this.editModal.title = "Edit Classification"
			this.editModal.record = record
			this.editModal.showDelete = true

			if ("Union" in record && record.Union)
				this.editModal.attachedToUnion = true
			else
				this.editModal.attachedToUnion = false
		},
		save() {
			let vm = this

			let payload = this.cloneObj(this.editModal.payload)

			let deleteObjects = [
				"UpdatedBy",
				"User",
				"Union",
				"ClassificationGroup",
				"unionName",
				"chargeOutGroup",
				"employeeCount",
				"rowClasses",
				"selected"
			]

			deleteObjects.forEach(key => {
				delete payload[key]
			})

			this.saveRecord("EmployeeClassification",payload,function(){
				vm.editModal.visible = false
				vm.alertSaved("Classification saved!")
				vm.fetchClassifications()
				vm.editModal.payload = {}
			})
		},
		deleteItem() {
			let vm = this

			this.deleteRecord('EmployeeClassification',this.editModal.record.id,function(){
				vm.editModal.visible = false;
				vm.alertSuccess("Classification deleted!")
				vm.fetchClassifications()
			})
		},
		newClassification() {
			let vm = this

			vm.editModal.title = "New Classification"
			vm.editModal.payload = {
				title: "",
				default_hourly_rate: null,
				default_cost_hr: null
			}
			vm.editModal.visible = true
			vm.editModal.showDelete = false
		},
		fetchOptions() {
			let vm = this

			let promises = [
				this.getJSON("Union/get"),
				this.getJSON("ClassificationGroup/get"),
				this.getJSON("TimeLogSetting/get")
			]

			this.spinner(true)

			Promise.all(promises).then(responses => {

				if (responses[0].success)
					vm.unions = responses[0].data

				if (responses[1].success)
					vm.groups = responses[1].data

				if (responses[2].success && responses[2].data.length > 0)
					vm.timeLogSettings = responses[2].data[0]

				vm.spinner(false)
			})
		},
		fetchClassifications() {
			let vm = this

			this.spinner(true)

			let params = {
				include: [
				{
					model:"User",
					fields: ["id"],
					include: [{
						model: "UserPermission",
						fields: ["disconnect_date","invite_accepted"]
					}]
				},
				"Union","ClassificationGroup"]
			}

			let includes = JSON.stringify(["User","Union","ClassificationGroup"])
			this.getJSON("EmployeeClassification/get",params).then(function(response) {
				if (response.success)
					vm.$store.employeeClassifications = response.data
				
				vm.spinner(false)
			})	

		}
	},
	created() {
		this.fetchClassifications()
		this.fetchOptions()

		let vm = this
		//list for new record button click event from parent
		this.$bus.$on("newEmployeeClassification", vm.newClassification)
	}

}
</script>

